
import { Component, Prop } from 'vue-property-decorator';
import { Member, Score, Scores } from '@/models/member';
import { SchemeNodeModel } from '@/models/schemeNodeModel';
import moment from 'moment';
import { GroupSchemeScope } from '@/components/mixins/schemescope';
import { mixins } from 'vue-class-component';
import { isAssessible } from '@/services/utils';

@Component({
  name: 'NodeAssessment',
})
export default class NodeAssessment extends mixins(GroupSchemeScope) {
  private updatingToGrade: number | null = null;
  /**
   * schemeNode
   * There is an instance of this component for each assessable scheme node, this is the node belonging to that instance.
   *
   * @property {SchemeNodeModel}
   */
  @Prop() schemeNode!: SchemeNodeModel;

  /**
   * memberUuid
   * ID ref for the current member.
   *
   * @property {memberUuid}
   */
  @Prop() private memberUuid!: string;

  /**
   * Returns the member from the store via the ID.
   * NOTE - The store value will be updated which is why this will be fetched like this and not passed in the params.
   *
   * @return Array<Member>>
   */
  get member(): Member {
    return this.$store.getters['groupMembers/memberById'](this.groupId, this.memberUuid);
  }

  /**
   * Responsible for calculating the available assessment options for each skill scheme node.
   *
   * @return Array<{id: string, label: string}>>
   */
  get schemeNodeAssessmentButtons(): Array<{ id: number; label: string }> {
    const assessmentButtons = [] as Array<{ id: number; label: string }>;
    // Assessment options for the node.
    if (isAssessible(this.schemeNode)) {
      // Are the assessments binary pass/fail or graded.  Get the matching labels.
      const labels: string[] = this.schemeNode.grade ? this.schemeGrades[this.schemeNode.grade].labels || [] : [];

      // Add a default Not Assessed assessment button, delete on click
      assessmentButtons.push({ id: -1, label: this.$t('assessments.unassessed') });

      // A button is going to be created for each label.  The ID for the button will match the grade int.
      labels.forEach((label, index) => assessmentButtons.push({ id: index, label: label }));
    } else {
      // We should not end up here due to condition set for passing to this component but something went wrong.
      console.error('[Assessment] - Could not determine assessment scheme for scheme node ', this.schemeNode.id);
    }
    return assessmentButtons;
  }

  get score(): Score | undefined {
    const memberScores: Scores = this.member.scores;
    return memberScores[this.schemeNode.id] as Score;
  }

  get scoreLastUpdated(): string {
    return this.score
      ? this.$t('assessments.last_assessed', { ago: moment.unix(Number(this.score.ts)).fromNow() })
      : '';
  }

  get scoreLastUpdatedFull(): string {
    return this.score ? moment.unix(Number(this.score.ts)).format('YYYY-MM-DD HH:mm') : '';
  }

  get isChangedScore(): boolean {
    const loginTs = localStorage?.getItem('loginTs');
    return !!(loginTs && this.score && this.score.ts > loginTs);
  }

  get currentDbGrade(): number {
    if (this.score) {
      return Number(this.score.assessment);
    }
    return -1; // default to unassessed
  }

  /**
   * Will check the members scores so the default selected button for the skill can reflect correct state.
   *
   * @return {number}
   */
  get currentGrade(): number {
    if (this.updatingToGrade !== null) {
      // while we are in the middle of an update, we ignore the member DB, since that might be being updated by a previous
      // click on a different skill, and we return what the latest click set the score to.
      return this.updatingToGrade;
    }
    return this.currentDbGrade;
  }

  /**
   * Unused setter for the current grade, is here because Vue gets a headache without it, setter addict needs help!.
   *
   * @param {number} value
   * @return {void}
   */
  set currentGrade(value) {
    // not set here
  }

  /**
   * If a change to this component instances skill node this will fire off the new selection to the API,
   * in future will use DB if offline.
   *
   * @param  {number} assessment
   * @return {void}
   */
  async updateAssessment(assessment: number) {
    this.updatingToGrade = assessment;
    const timeSinceAssessment = this.score ? moment().unix() - Number(this.score.ts) : 99;
    if (assessment === this.currentDbGrade && timeSinceAssessment <= 5) {
      console.warn('Trying to reassess the same grade within 5 seconds');
      this.updatingToGrade = null;
      return;
    }
    if (!(await this.assessNode(this.member.uuid, this.schemeNode, assessment))) {
      this.$alert('error', this.$t('members.error_assessing_skill'));
    }

    this.updatingToGrade = null;
  }
}
